import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Link } from "react-router-dom";
import { MagnifyingGlass, ChartLineUp, Warning, CursorClick } from "@phosphor-icons/react";

/**
 * TopSearchesTile — admin telemetry for the global navbar search.
 *
 * Three views in one card:
 *   • Top queries (by volume + click-through rate)
 *   • Zero-result queries (discovery / naming gaps to flag to owners)
 *   • Top clicked trucks (which trucks customers reach via search)
 *
 * Data source: `/api/admin/kpis/top-searches?days=30` aggregating the
 * `truck_search_query` and `truck_search_click` events that
 * `routers/truck_search.py` writes on every navbar search and result
 * click.
 */
export default function TopSearchesTile() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/admin/kpis/top-searches?days=${days}`)
      .then((res) => { if (!cancelled) setData(res.data || null); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className="space-y-4" data-testid="admin-top-searches-tile">
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
          Last
        </span>
        {[7, 30, 90].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setDays(n)}
            className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition ${
              days === n
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-foreground border-border hover:border-primary/40"
            }`}
            data-testid={`top-searches-range-${n}`}
          >
            {n} days
          </button>
        ))}
      </div>

      {err && (
        <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-900/20 p-3 text-sm text-rose-700 dark:text-rose-200">
          {err}
        </div>
      )}

      {loading && !data ? (
        <div className="text-xs text-muted-foreground italic">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Top queries */}
          <Section
            icon={<MagnifyingGlass weight="duotone" className="h-4 w-4 text-primary" />}
            title="Top searches"
            subtitle="Customers know what they're looking for"
            empty="No searches yet."
            rows={(data?.top_queries || []).map((q) => ({
              testId: `top-search-q-${q.query}`,
              left: (
                <span className="font-mono text-sm truncate" title={q.query}>
                  {q.query}
                </span>
              ),
              right: (
                <div className="text-right text-xs tabular-nums flex-none">
                  <div className="font-semibold">{q.searches.toLocaleString()}</div>
                  <div className="text-muted-foreground">{q.ctr_pct}% CTR</div>
                </div>
              ),
            }))}
          />

          {/* Zero-result queries */}
          <Section
            icon={<Warning weight="duotone" className="h-4 w-4 text-amber-600" />}
            title="Zero-result queries"
            subtitle="Discovery gaps — customers tried but found nothing"
            empty="None — every search returned at least one match."
            rows={(data?.zero_results || []).map((z) => ({
              testId: `zero-result-${z.query}`,
              left: (
                <span className="font-mono text-sm truncate text-amber-800 dark:text-amber-200" title={z.query}>
                  {z.query}
                </span>
              ),
              right: (
                <div className="text-right text-xs tabular-nums flex-none">
                  <div className="font-semibold">{z.hits.toLocaleString()}×</div>
                </div>
              ),
            }))}
          />

          {/* Top clicked trucks */}
          <Section
            icon={<CursorClick weight="duotone" className="h-4 w-4 text-emerald-600" />}
            title="Top clicked trucks"
            subtitle="Which trucks customers reach via search"
            empty="No search clicks yet."
            rows={(data?.top_clicked_trucks || []).map((t) => ({
              testId: `top-clicked-${t.truck_id}`,
              left: (
                <span className="text-sm truncate flex-1" title={t.truck_name}>
                  {t.truck_slug ? (
                    <Link
                      to={`/t/${t.truck_slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:underline"
                    >
                      {t.truck_name}
                    </Link>
                  ) : (
                    t.truck_name
                  )}
                </span>
              ),
              right: (
                <div className="text-right text-xs tabular-nums flex-none">
                  <div className="font-semibold">{t.clicks.toLocaleString()}</div>
                  <div className="text-muted-foreground">clicks</div>
                </div>
              ),
            }))}
          />
        </div>
      )}

      <div className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-2 border-t border-border">
        <ChartLineUp weight="duotone" className="h-3.5 w-3.5 flex-none mt-0.5" />
        <span>
          Volume is per-query. CTR = clicks ÷ searches for that query. Zero-result queries reveal naming / discoverability gaps — consider DMing the owner of the truck the customer was likely searching for, OR adding their nickname as an alias.
        </span>
      </div>
    </div>
  );
}

function Section({ icon, title, subtitle, empty, rows }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-3 py-2.5 border-b border-border bg-muted/30">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
      </div>
      {rows.length === 0 ? (
        <div className="px-3 py-6 text-xs text-muted-foreground italic text-center">
          {empty}
        </div>
      ) : (
        <ul className="divide-y divide-border max-h-72 overflow-y-auto">
          {rows.map((r, i) => (
            <li
              key={i}
              className="px-3 py-2 flex items-center gap-2"
              data-testid={r.testId}
            >
              {r.left}
              {r.right}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
